"use client";

import { ArrowUpRight } from "lucide-react";
import type { MouseEvent } from "react";
import { type MusicServiceLink, musicServiceLinks } from "@/lib/music/links";
import { cn } from "@/lib/utils";

/**
 * "Listen on" chips. Always leave the app's page: a new tab on the web, and
 * on native the OS takes the URL (Capacitor hands non-app hosts to the
 * system), which lets universal links land in the Music / Spotify / YouTube
 * Music app itself when it's installed.
 */
export function MusicServiceChips({
  song,
  className,
}: {
  song: { title: string; artistName: string; providerUrl: string | null };
  className?: string;
}) {
  const links = musicServiceLinks(song);
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {links.map((link) => (
        <a
          key={link.service}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => openInNewTab(event, link.href)}
          className="group flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface-alt py-1 pr-2 pl-1 whitespace-nowrap shadow-sm backdrop-blur-md transition-transform duration-150 ease-out-strong active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-10"
        >
          <ServiceGlyph service={link.service} />
          <span className="text-[11px] font-semibold text-text-secondary dark:text-white">
            {link.label}
          </span>
          <ArrowUpRight
            aria-hidden
            className="size-2.5 text-text-tertiary transition-transform duration-150 ease-out-strong pointer-fine:group-hover:translate-x-px pointer-fine:group-hover:-translate-y-px"
          />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      ))}
    </div>
  );
}

function openInNewTab(event: MouseEvent<HTMLAnchorElement>, href: string) {
  // Modified clicks keep the browser's own behaviour (background tab etc.).
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    return;
  }
  // Explicit, so no wrapper (link interception, an in-app frame) can turn
  // this into a same-page navigation. `noopener` makes the return value null
  // by spec, so there is nothing to check — and never a same-tab fallback.
  event.preventDefault();
  window.open(href, "_blank", "noopener,noreferrer");
}

/** Small brand marks, drawn inline so they need no asset requests. */
function ServiceGlyph({ service }: { service: MusicServiceLink["service"] }) {
  const common = "size-4.5 shrink-0";
  switch (service) {
    case "apple_music":
      return (
        <svg aria-hidden viewBox="0 0 20 20" className={common}>
          <defs>
            <linearGradient id="am-glyph" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fb5c74" />
              <stop offset="1" stopColor="#fa233b" />
            </linearGradient>
          </defs>
          <rect width="20" height="20" rx="5.5" fill="url(#am-glyph)" />
          <path
            fill="#fff"
            d="M13.6 4.6v7.6a1.9 1.9 0 1 1-1.2-1.75V7.1L8.2 8v5.4a1.9 1.9 0 1 1-1.2-1.75V6.2z"
          />
        </svg>
      );
    case "spotify":
      return (
        <svg aria-hidden viewBox="0 0 20 20" className={common}>
          <circle cx="10" cy="10" r="10" fill="#1ed760" />
          <g
            fill="none"
            stroke="#000"
            strokeLinecap="round"
            strokeOpacity="0.85"
          >
            <path strokeWidth="1.6" d="M5.2 7.6c3.2-1 6.9-.7 9.7.9" />
            <path strokeWidth="1.35" d="M5.8 10.3c2.7-.8 5.6-.5 7.9.8" />
            <path strokeWidth="1.15" d="M6.3 12.8c2.2-.6 4.3-.4 6.1.6" />
          </g>
        </svg>
      );
    case "youtube_music":
      return (
        <svg aria-hidden viewBox="0 0 20 20" className={common}>
          <circle cx="10" cy="10" r="10" fill="#ff0033" />
          <circle
            cx="10"
            cy="10"
            r="5.4"
            fill="none"
            stroke="#fff"
            strokeWidth="1.2"
          />
          <path fill="#fff" d="M8.6 7.6 12.4 10l-3.8 2.4z" />
        </svg>
      );
  }
}
